import { routing } from "@/i18n/routing";

const BASE_URL = "https://vide-maison-bruxelles.be";

// docs/i18n/ROLLBACK_I18N_SEO_VIDE_MAISON_BRUXELLES.md: French lives at the
// bare root, never under /fr — only nl/en carry a URL prefix.
//
// `pathname` is locale-agnostic, always starts with "/" and is "" for the
// homepage — never "/". Passing "/" would concatenate into "/nl/" for the
// prefixed locales, i.e. a canonical pointing at a URL that itself 308s to
// "/nl" (gap VMB-02 of docs/audits/SEO_TECHNICAL_VMB_POST_SERP.md). The
// homepage is normalised to "/" on the way out instead, because Next's
// metadata resolver treats an empty-string canonical as absent.
export function localizedPath(locale, pathname) {
  const path = locale === routing.defaultLocale ? pathname : `/${locale}${pathname}`;
  return path === "" ? "/" : path;
}

// Builds the `alternates` block for a page's generateMetadata: a
// self-canonical (unprefixed for fr, /nl or /en prefixed otherwise) plus an
// hreflang `languages` map — fr-BE/nl-BE/en, each pointing at the real URL
// that locale actually uses, plus x-default pointing at French (the
// canonical default for undeclared-language visitors, e.g. Googlebot's
// default crawl). Matches the shape used by src/app/sitemap.xml/route.js so
// search engines see one consistent signal everywhere.
export function buildAlternates(locale, pathname) {
  return {
    canonical: localizedPath(locale, pathname),
    languages: {
      "fr-BE": localizedPath("fr", pathname),
      "nl-BE": localizedPath("nl", pathname),
      en: localizedPath("en", pathname),
      "x-default": localizedPath("fr", pathname),
    },
  };
}

// Absolute-URL equivalent for JSON-LD and the sitemap (schema.org and
// sitemaps require full URLs, not metadataBase-relative paths like the
// Next.js Metadata API accepts above). The French homepage collapses to the
// bare origin — same URL Next itself emits for a "/" canonical, so the
// sitemap, the canonical and the JSON-LD keep declaring one identical string.
export function absoluteLocalizedUrl(locale, pathname = "") {
  const path = localizedPath(locale, pathname);
  return path === "/" ? BASE_URL : `${BASE_URL}${path}`;
}

// og:locale codes, distinct from next-intl's bare locale codes (fr/nl/en) —
// Open Graph expects language_TERRITORY.
const OG_LOCALES = { fr: "fr_BE", nl: "nl_BE", en: "en_GB" };

// Builds a page's complete `openGraph` metadata block.
//
// Why every page needs its own: Next.js does NOT merge `openGraph` across
// layout and page — a page-level openGraph replaces the root layout's one
// entirely. Before this helper, no page declared one, so every route
// inherited the layout's hardcoded `url: "https://vide-maison-bruxelles.be"`
// and advertised the homepage as its og:url while its canonical was correct
// (gap VMB-04 of docs/audits/SEO_TECHNICAL_VMB_POST_SERP.md).
//
// `title`/`description` are deliberately left out: Next fills og:title and
// og:description from the page's own resolved metadata when the openGraph
// block omits them, so each page gets its real title instead of the
// site-wide fallback. Pass them through `overrides` for a social-specific
// wording.
export function buildOpenGraph(locale, pathname, overrides = {}) {
  return {
    url: absoluteLocalizedUrl(locale, pathname),
    siteName: "Vide Maison Bruxelles",
    locale: OG_LOCALES[locale],
    type: "website",
    ...overrides,
  };
}
